import { Link } from 'react-router-dom';
import { usePageMeta } from '@/hooks/usePageMeta';
import ContactForm from '@/components/feature/ContactForm';

const features = [
  { icon: 'ri-movie-2-line', title: '剧集浏览', desc: '按题材分类浏览最新韩剧，支持年份、评分与播出状态多条件筛选。' },
  { icon: 'ri-file-list-3-line', title: '剧情介绍', desc: '提供剧集详细剧情简介与分集剧情，追剧不迷路。' },
  { icon: 'ri-user-star-line', title: '演员资料', desc: '收录演员简介与主要代表作，一键跳转到对应剧集详情。' },
  { icon: 'ri-newspaper-line', title: '资讯动态', desc: '剧情分析、演员动态与行业观察，了解韩剧台前幕后的故事。' },
];

const faqs = [
  { q: '网站提供在线观看吗？', a: '不提供。本站仅用于韩剧内容的展示与资讯浏览，不包含任何播放、下载或会员功能。' },
  { q: '收藏功能会同步到账号吗？', a: '不会。收藏仅保存在你的浏览器本地，刷新页面后仍然保留，但不会上传到服务器。' },
  { q: '评论区的评论是真实的吗？', a: '目前评论区为前端演示内容，你发布的评论只在本页面临时显示，刷新后不会保留。' },
  { q: '如何联系你们？', a: '可以通过本页的联系表单或页面底部的邮箱与我们取得联系，我们会尽快回复。' },
];

export default function About() {
  usePageMeta(
    '关于我们 - 韩剧影视内容平台 - 韩剧',
    '了解韩剧网站的定位与服务范围：专注韩剧剧集展示、剧情介绍、演员资料与资讯服务，并提供联系方式与使用说明。',
  );

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-600 cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <h1 className="font-heading font-bold text-2xl md:text-4xl text-foreground-950">关于「韩剧」</h1>
          <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
            「韩剧」是一个专注韩国电视剧内容展示与资讯服务的影视内容平台，致力于为韩剧爱好者提供清晰、专业、舒适的追剧浏览体验。
          </p>
        </div>
      </div>

      <div className="w-full max-w-[1280px] mx-auto px-4 md:px-8 py-10 md:py-14">
        <section>
          <h2 className="font-heading font-bold text-2xl text-foreground-950">网站定位与服务范围</h2>
          <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed max-w-4xl">
            我们面向广大韩剧观众，尤其是希望了解最新韩剧、查看剧情介绍与演员资料的用户。网站主要提供韩剧的分类浏览、剧集详情查看与剧情资讯服务。
            需要特别说明的是，本站不提供任何播放、下载及会员功能，也不涉及任何在线支付或接口调用，所有内容均用于浏览与资讯展示。
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {features.map((f, i) => (
              <div
                key={f.title}
                className={`rounded-lg p-5 border ${i === 0 ? 'bg-primary-500 border-primary-500 text-white' : 'bg-background-50 border-background-200'}`}
              >
                <span className={`w-11 h-11 rounded-lg flex items-center justify-center ${i === 0 ? 'bg-white/15 text-white' : 'bg-primary-100 text-primary-600'}`}>
                  <i className={`${f.icon} text-xl`}></i>
                </span>
                <h3 className={`mt-4 font-semibold ${i === 0 ? 'text-white' : 'text-foreground-950'}`}>{f.title}</h3>
                <p className={`mt-2 text-sm leading-relaxed ${i === 0 ? 'text-white/85' : 'text-foreground-600'}`}>{f.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-heading font-bold text-2xl text-foreground-950">使用说明与常见问题</h2>
          <div className="mt-5 space-y-3">
            {faqs.map((f) => (
              <div key={f.q} className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="font-semibold text-foreground-900 flex items-start gap-2">
                  <i className="ri-question-line text-primary-500 mt-0.5"></i> {f.q}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start">
            <div>
              <h2 className="font-heading font-bold text-2xl text-foreground-950">联系我们</h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                如果你对内容有任何建议、发现了信息错误，或希望与我们合作，欢迎通过下方表单或邮件与我们联系。
              </p>
              <div className="mt-6 space-y-4">
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className="ri-mail-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">邮箱</p>
                    <p className="text-sm text-foreground-900">service@hanju.example</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                    <i className="ri-map-pin-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">地址</p>
                    <p className="text-sm text-foreground-900">韩国首尔特别市麻浦区上岩洞</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-11 h-11 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                    <i className="ri-time-line text-xl"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-400">响应时间</p>
                    <p className="text-sm text-foreground-900">工作日 1-2 个工作日内回复</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-background-200 bg-background-100 p-5 md:p-6">
              <h3 className="font-heading font-bold text-lg text-foreground-950 mb-4">给我们留言</h3>
              <ContactForm />
            </div>
          </div>
        </section>

        <section className="mt-14 rounded-lg bg-secondary-900 p-6 md:p-8">
          <h2 className="font-heading font-bold text-xl text-white">版权声明</h2>
          <p className="mt-3 text-sm text-secondary-200 leading-relaxed">
            本站所展示的剧集名称、简介、演员资料与资讯内容均为演示用途，用于呈现网站结构与功能。本站不提供任何影视播放与下载服务，
            所有内容版权归原权利人所有。如涉及内容问题，请通过上方联系方式与我们沟通，我们将及时处理。
          </p>
        </section>
      </div>
    </div>
  );
}